import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CATCHMENT_TOUR_STEPS, tourSpotlightIds } from './catchmentTour';

const DIM_SOFT = 'rgba(15, 23, 42, 0.28)';
const DIM_STRONG = 'rgba(15, 23, 42, 0.62)';
const SPOTLIGHT_GREEN = '#39FF14';
const SPOTLIGHT_PAD = 8;
const cutoutRing = (dim, punchThrough) =>
  punchThrough
    ? `0 0 0 3px ${SPOTLIGHT_GREEN}, 0 0 16px ${SPOTLIGHT_GREEN}, 0 0 0 9999px ${dim}`
    : `0 0 0 3px ${SPOTLIGHT_GREEN}, 0 0 16px ${SPOTLIGHT_GREEN}`;

const cardPositionSx = (align) => {
  if (align === 'bottom-left') return { bottom: 20, left: 20, top: 'auto', right: 'auto' };
  if (align === 'bottom-right') return { bottom: 20, right: 20, top: 'auto', left: 'auto' };
  if (align === 'top-left') return { top: 20, left: 20, right: 'auto', bottom: 'auto' };
  return { top: 20, right: 20, left: 'auto', bottom: 'auto' };
};

const measureNode = (node) => {
  if (!node || typeof node.getBoundingClientRect !== 'function') return null;
  const next = node.getBoundingClientRect();
  if (!next.width || !next.height) return null;
  return {
    top: next.top - SPOTLIGHT_PAD,
    left: next.left - SPOTLIGHT_PAD,
    width: next.width + SPOTLIGHT_PAD * 2,
    height: next.height + SPOTLIGHT_PAD * 2,
  };
};

const findTourTargets = (ids) => {
  if (!ids?.length || typeof document === 'undefined') return [];
  return ids
    .map((id) => document.querySelector(`[data-catchment-tour="${id}"]`))
    .filter(Boolean);
};

const EMPTY_STEP = {};

const dimMaskSx = (rects) => {
  if (!rects.length) return {};
  const layers = ['linear-gradient(#000 0 0)', ...rects.map(() => 'linear-gradient(#000 0 0)')];
  const sizes = ['100% 100%', ...rects.map((r) => `${r.width}px ${r.height}px`)];
  const positions = ['0 0', ...rects.map((r) => `${r.left}px ${r.top}px`)];
  return {
    bgcolor: DIM_STRONG,
    WebkitMaskImage: layers.join(', '),
    maskImage: layers.join(', '),
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: sizes.join(', '),
    maskSize: sizes.join(', '),
    WebkitMaskPosition: positions.join(', '),
    maskPosition: positions.join(', '),
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  };
};

const StudentCatchmentTour = ({
  steps = CATCHMENT_TOUR_STEPS,
  step = 0,
  onStepChange,
  onDismiss,
  layoutKey,
  testId = 'catchment-tour',
}) => {
  const safeStep = Math.min(Math.max(step, 0), Math.max(steps.length - 1, 0));
  const current = steps[safeStep] || EMPTY_STEP;
  const spotlightKey = useMemo(
    () => tourSpotlightIds(current).join('|'),
    [current]
  );
  const spotlightIds = useMemo(
    () => (spotlightKey ? spotlightKey.split('|') : []),
    [spotlightKey]
  );
  const isFirst = safeStep === 0;
  const isLast = safeStep === steps.length - 1;
  const [rects, setRects] = useState([]);

  useLayoutEffect(() => {
    const targets = findTourTargets(spotlightIds);
    if (!targets.length) {
      setRects([]);
      return undefined;
    }

    const measure = () => setRects(targets.map(measureNode).filter(Boolean));
    measure();
    targets.forEach((target) => {
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      }
    });
    measure();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    targets.forEach((target) => resizeObserver?.observe(target));
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    const retry = window.setTimeout(measure, 350);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      window.clearTimeout(retry);
    };
  }, [spotlightIds, safeStep, layoutKey]);

  const hasCutout = rects.length > 0;
  const dim = hasCutout ? DIM_STRONG : DIM_SOFT;
  const useMask = rects.length > 1;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
      data-testid={testId}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          ...(useMask ? dimMaskSx(rects) : { bgcolor: hasCutout ? 'transparent' : dim }),
        }}
      />
      {rects.map((rect, index) => (
        <Box
          key={`${rect.top}-${rect.left}-${index}`}
          aria-hidden
          data-testid={index === 0 ? `${testId}-spotlight` : `${testId}-spotlight-${index}`}
          sx={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 2,
            boxShadow: cutoutRing(dim, hasCutout && !useMask && index === 0),
            pointerEvents: 'none',
          }}
        />
      ))}

      <Paper
        role="dialog"
        aria-labelledby={`${testId}-title`}
        aria-describedby={`${testId}-body`}
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          position: 'absolute',
          ...cardPositionSx(current.cardAlign),
          width: { xs: 'calc(100% - 40px)', sm: 360 },
          maxWidth: 360,
          p: 2,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {safeStep + 1} of {steps.length}
            </Typography>
            <Typography id={`${testId}-title`} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {current.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onDismiss} aria-label="Skip tour">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography id={`${testId}-body`} variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {current.body}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button size="small" onClick={onDismiss} sx={{ textTransform: 'none' }}>
            Skip
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={isFirst}
              onClick={() => onStepChange(safeStep - 1)}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => {
                if (!isLast) {
                  onStepChange(safeStep + 1);
                  return;
                }
                if (typeof window.scrollTo === 'function') {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }
                onDismiss();
              }}
              sx={{ textTransform: 'none' }}
            >
              {isLast ? 'Done' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default StudentCatchmentTour;
